
// src/pages/MyExchange.tsx
import React, { useEffect, useState } from 'react';
import Header from '@/components/Header';
import { config } from '@/config';

const EXCHANGES = ['kraken', 'bybit', 'mexc', 'kucoin'];

interface ExchangeConnection {
  id: string;
  exchange: string;
  label?: string;
  createdAt: string;
  updatedAt: string;
}

export default function MyExchange() {
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ 
    id: '', 
    exchange: EXCHANGES[0], 
    label: '', 
    apiKey: '', 
    privateKey: '', 
    passphrase: '' 
  });
  const [status, setStatus] = useState('');
  const [connections, setConnections] = useState<ExchangeConnection[]>([]);

  useEffect(() => {
    loadConnections();
  }, []);

  // Version indicator - this will change when code is deployed
  const VERSION = "v1.0.2 - Build #3";

  async function loadConnections() {
    try {
      const res = await fetch(`${config.apiBase}/user/exchanges`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        // API returns { ok: true, exchanges: [...] }
        const exchanges = data.exchanges || [];
        setConnections(exchanges);
        if (exchanges[0]) {
          setForm(prev => ({ 
            ...prev, 
            id: exchanges[0].id, 
            exchange: exchanges[0].exchange, 
            label: exchanges[0].label || '' 
          }));
        }
      }
    } catch (error) {
      console.error('Failed to load connections:', error);
    } finally {
      setLoading(false);
    }
  }

  async function onSave() {
    setStatus('Saving...');
    try {
      const { privateKey, ...formData } = form;
      const body = { ...formData, apiSecret: privateKey };
      
      console.log('Saving exchange connection:', { ...formData, apiSecret: '***' });
      
      const res = await fetch(`${config.apiBase}/user/exchanges`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${res.status}: ${res.statusText}`);
      }
      
      const data = await res.json();
      if (data.ok) {
        setForm(prev => ({ ...prev, id: "", apiKey: '', privateKey: '' }));
        setStatus('✅ Saved successfully!');
        await loadConnections();
        
        // Clear status after 3 seconds
        setTimeout(() => setStatus(''), 3000);
      } else {
        throw new Error(data.error || 'Save failed');
      }
    } catch (error) {
      console.error('Save error:', error);
      setStatus(`❌ Error: ${error instanceof Error ? error.message : 'Failed to save'}`);
      
      // Clear error status after 5 seconds
      setTimeout(() => setStatus(''), 5000);
    }
  }

  async function onTest() {
    if (!form.id) { 
      setStatus('💡 Save first, then test.'); 
      return; 
    }
    
    setStatus('🔄 Testing connection...');
    try {
      const res = await fetch(`${config.apiBase}/user/exchanges/test`, { 
        method: 'POST',
        headers: { "Content-Type": "application/json" },
        credentials: 'include',
        body: JSON.stringify({ id: form.id, exchange: form.exchange })
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${res.status}: ${res.statusText}`);
      }
      
      const data = await res.json();
      if (data.ok) {
        setStatus('✅ Connected successfully!');
        // Clear status after 3 seconds
        setTimeout(() => setStatus(''), 3000);
      } else {
        throw new Error(data.error || 'Connection test failed');
      }
    } catch (error) {
      console.error('Test error:', error);
      setStatus(`❌ Test failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      
      // Clear error status after 5 seconds
      setTimeout(() => setStatus(''), 5000);
    }
  }

  async function onDelete() {
    if (!form.id) return;
    
    if (!window.confirm('Are you sure you want to delete this exchange connection? This action cannot be undone.')) {
      return;
    }
    
    setStatus('🗑️ Deleting...');
    try {
      const res = await fetch(`${config.apiBase}/user/exchanges/${form.id}`, { 
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${res.status}: ${res.statusText}`);
      }
      
      const data = await res.json();
      if (data.ok) {
        setForm({ id: '', exchange: EXCHANGES[0], label: '', apiKey: '', privateKey: '', passphrase: '' });
        setStatus('✅ Deleted successfully!');
        await loadConnections();
        
        // Clear status after 3 seconds
        setTimeout(() => setStatus(''), 3000);
      } else {
        throw new Error(data.error || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      setStatus(`❌ Delete failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      
      // Clear error status after 5 seconds
      setTimeout(() => setStatus(''), 5000);
    }
  }

  if (loading) return <div className="flex justify-center p-8">Loading…</div>;

  return (
    <>
      <Header />
      <div className="max-w-6xl mx-auto p-3 md:p-6">
        {/* Version Indicator */}
        <div className="mb-4 p-2 bg-blue-100 border border-blue-300 rounded text-center">
          <span className="text-sm font-mono text-blue-800">MyExchange Version: {VERSION}</span>
        </div>
        
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">My Exchange Connection</h1>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Exchange</label>
            <select 
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={form.exchange} 
              onChange={e => setForm({ ...form, exchange: e.target.value })}
            >
              {EXCHANGES.map(x => (
                <option key={x} value={x}>{x.toUpperCase()}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Label (optional)</label>
            <input 
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={form.label} 
              onChange={e => setForm({ ...form, label: e.target.value })}
              placeholder="My Kraken Account"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">API Key</label>
            <input 
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Paste your API key here" 
              value={form.apiKey} 
              onChange={e => setForm({ ...form, apiKey: e.target.value })}
              type="password"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Private Key</label>
            <input 
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Paste your private key here" 
              value={form.privateKey} 
              onChange={e => setForm({ ...form, privateKey: e.target.value })}
              type="password"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Passphrase (if required)</label>
            <input 
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={form.passphrase} 
              onChange={e => setForm({ ...form, passphrase: e.target.value })}
              type="password"
              placeholder="Optional passphrase"
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <button 
              className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              onClick={onSave}
            >
              Save
            </button>
            <button 
              className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              onClick={onTest}
            >
              Test Connection
            </button>
            <button 
              className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              onClick={onDelete}
            >
              Delete
            </button>
          </div>
          
          {status && (
            <div className="text-sm text-gray-600 bg-gray-50 rounded-md p-3">
              {status}
            </div>
          )}
          
          <div className="text-xs text-gray-500 bg-blue-50 rounded-md p-3">
            <p>🔒 Keys are encrypted at rest. Enable <strong>read + trade only</strong>; disable <strong>withdrawals</strong> on your exchange.</p>
          </div>
        </div>
      </div>

      {connections.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Current Connections</h2>
          <div className="space-y-3">
            {connections.map(conn => (
              <div key={conn.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                <div>
                  <div className="font-medium text-gray-900">{conn.exchange.toUpperCase()}</div>
                  {conn.label && <div className="text-sm text-gray-600">{conn.label}</div>}
                  <div className="text-xs text-gray-500">Created: {new Date(parseInt(conn.createdAt) * 1000).toLocaleDateString()}</div>
                </div>
                <button
                  className="text-sm text-red-600 hover:text-red-800"
                  onClick={() => setForm({ ...form, id: conn.id, exchange: conn.exchange, label: conn.label || '' })}
                >
                  Edit
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
        </div>
      </div>
    </>
  );
}
